import { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import type { ImageUpload } from '../types/ImageUpload';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { red } from '@mui/material/colors';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import { buildImageUrl } from '../utils/images';
import { licenses } from '../utils/licenses';
import MaterialLink from './material-link';
import MaterialLinkButton from './material-link-button';
import { momentParse } from '../utils/time';

const useStyles = makeStyles()((theme) => ({
  thumbnail: {
    height: 142,
    width: 142,
    display: 'block',
    margin: theme.spacing(0, 1, 1, 0),
  },
  mobileThumbnail: {
    height: 100,
    width: 100,
  },
  metaHeader: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(0.5),
    '&:not(:first-of-type)': {
      marginTop: theme.spacing(2),
    },
  },
  user: {
    display: 'flex',
    alignItems: 'center',
  },
  createdAt: {
    marginTop: theme.spacing(0.5),
  },
  avatar: {
    marginRight: theme.spacing(0.5),
    width: 20,
    height: 20,
  },
  licenseBlock: {
    marginTop: theme.spacing(2),
  },
  license: {
    borderColor: theme.palette.divider,
  },
  deleteButton: {
    marginTop: theme.spacing(2),
    color: `${red.A400} !important`,
    borderColor: `${red.A400} !important`,
  },
}));

type Props = {
  imageUpload: ImageUpload;
};

const ImageUploadGalleryItem = ({ imageUpload }: Props) => {
  const { classes, cx } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    image,
    license,
    createdByUser,
    permissions,
    status,
  } = imageUpload;

  const { width, height } = useMemo(() => {
    if (image.width > 3000 || image.height > 3000) {
      if (image.width > image.height) {
        return {
          height: image.height * 3000 / image.width,
          width: 3000,
        };
      } else {
        return {
          width: image.width * 3000 / image.height,
          height: 3000,
        };
      }
    }
    return image;
  }, [image]);

  const createdAt = useMemo(() => momentParse(imageUpload.createdAt), [imageUpload]);

  // catch it if it got deleted recently
  if (status === 'DELETED') {
    return null;
  }

  return (
    <div
      className={cx('pswp-gallery__item', classes.thumbnail, { [classes.mobileThumbnail]: isMobile })}
    >
      <a
        href={buildImageUrl(image, 'gallery-large')}
        data-pswp-width={width}
        data-pswp-height={height}
        data-cropped="true"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={buildImageUrl(image, `gallery-thumb-crop-${isMobile ? '100' : '142'}`)} alt="Photo" />
      </a>
      <div className="pswp-caption-content">
        <div className={classes.metaHeader}>Uploaded by</div>
        <MaterialLink
          href={`/people/${createdByUser.username}`}
          className={classes.user}
        >
          {createdByUser.avatar && (
            <Avatar
              alt={createdByUser.displayName}
              src={createdByUser.avatar.small.url}
              className={classes.avatar}
            />
          )}
          {createdByUser.displayName}
        </MaterialLink>
        <div className={classes.createdAt}>
          {createdAt.format('MMMM D, YYYY h:mm a')}
        </div>
        <div className={classes.licenseBlock}>
          <Chip
            clickable
            component="a"
            href={licenses[license].href}
            label={`License: ${licenses[license].shortLabel}`}
            title={licenses[license].label}
            variant="outlined"
            className={classes.license}
          />
        </div>
        {permissions.editable && (
          <MaterialLinkButton
            href="/image-uploads"
            className={classes.deleteButton}
            size="small"
            variant="outlined"
          >
            Delete
          </MaterialLinkButton>
        )}
      </div>
    </div>
  );
};

export default ImageUploadGalleryItem;
